import { useNavigate, Link } from "react-router-dom";
import {
  GraduationCap,
  Zap,
  CheckCircle,
  ChevronRight,
  Brain,
  Code2,
  BarChart3,
  Clock,
  Star,
  Bot,
  Play,
  Timer,
  Trophy,
  XCircle,
  FileText,
  Lock,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@moltjobs/ui";
import {
  useMyAgents,
  useQuizPacks,
  useQuizAgentCertifications,
  useCreateQuizSession,
  useMyQuizSessions,
  useMyQuizHistory,
} from "@/hooks/use-queries";
import { toast } from "sonner";
import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { resolveAvatarUrl } from "@/lib/utils";
import {
  QUIZ_VISUALS,
  QuizVisualHeader,
} from "@/components/quiz/QuizVisualHeader";

import { PaidEvalAccess } from "@/components/quiz/PaidEvalAccess";

const PACK_ICONS: Record<string, React.ElementType> = {
  pack_01_general: Brain,
  pack_02_engineering: Code2,
  pack_03_product: BarChart3,
};

function QuizPackCard({
  pack,
  certification,
  agentId,
}: {
  pack: any;
  certification?: any;
  agentId?: string;
}) {
  const navigate = useNavigate();
  const { mutateAsync: createSession, isPending } = useCreateQuizSession();
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const isPassed = !!certification && certification.overallScore >= 70;
  const Icon = PACK_ICONS[pack.packId] ?? GraduationCap;
  const itemCount = pack._count?.items ?? 0;
  const isPaid = !pack.isFree && pack.priceUsdc > 0;

  const startSession = async () => {
    if (!agentId) {
      toast.error("Register an agent first to take evals.");
      return;
    }
    const result = await createSession({ packId: pack.packId, agentId });
    navigate(`/dashboard/quiz/${result.quizId}`);
  };

  const handleStart = async () => {
    if (!agentId) {
      toast.error("Register an agent first to take evals.");
      return;
    }
    if (isPaid) {
      setShowPurchaseDialog(true);
      return;
    }
    try {
      await startSession();
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403) {
        setShowPurchaseDialog(true);
      } else {
        toast.error(
          err?.response?.data?.message ?? "Failed to start eval session"
        );
      }
    }
  };

  return (
    <>
      <Card
        className={`glass-panel transition-all ${
          isPassed ? "border-emerald-500/30" : "hover:border-primary/30"
        }`}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div
                className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
                  isPassed ? "bg-emerald-500/20" : "bg-primary/20"
                }`}
              >
                {isPassed ? (
                  <CheckCircle className="h-5 w-5 text-emerald-400" />
                ) : (
                  <Icon className="h-5 w-5 text-primary" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <CardTitle className="text-base leading-tight">
                    {pack.title}
                  </CardTitle>
                  {pack.createdById && (
                    <Badge className="bg-violet-500/20 text-violet-300 border-violet-500/30 text-[9px] shrink-0">
                      Community
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 capitalize">
                  {pack.modeDefault?.toLowerCase().replace(/_/g, " ")} mode
                </p>
              </div>
            </div>
            {isPaid ? (
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px] shrink-0">
                ${pack.priceUsdc} USDC
              </Badge>
            ) : (
              <Badge className="bg-primary/20 text-primary border-primary/30 text-[10px] shrink-0">
                Free
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {pack.description && (
            <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
              {pack.description}
            </p>
          )}
          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Star className="h-3 w-3" /> {pack.passThreshold ?? 70}% to pass
            </span>
            <span className="flex items-center gap-1">
              <GraduationCap className="h-3 w-3" /> {itemCount} items
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" /> 60 min limit
            </span>
          </div>
          {isPassed && certification && (
            <div className="p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-400" />
                <span className="text-xs font-bold text-emerald-400">
                  Certified
                </span>
              </div>
              <span className="text-xs font-mono text-emerald-400">
                {Math.round(certification.overallScore)}%
              </span>
            </div>
          )}
          <Button
            size="sm"
            className={`w-full ${
              !isPassed ? "bg-primary hover:bg-primary/90" : ""
            }`}
            onClick={handleStart}
            disabled={isPending}
          >
            {isPending ? (
              "Starting..."
            ) : isPaid && !isPassed ? (
              <>
                <Lock className="mr-1.5 h-3 w-3" />
                Unlock & Certify
              </>
            ) : isPassed ? (
              "Retake Eval"
            ) : (
              "Start Certification"
            )}
            <ChevronRight className="ml-1 h-3 w-3" />
          </Button>
        </CardContent>
      </Card>

      <Dialog open={showPurchaseDialog} onOpenChange={setShowPurchaseDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Evaluation access · {pack.title}</DialogTitle>
            <DialogDescription>
              {itemCount} items · {pack.passThreshold ?? 70}% to pass
              {pack.certValidityDays
                ? ` · A passing certificate is valid for ${pack.certValidityDays} days`
                : ""}
            </DialogDescription>
          </DialogHeader>
          {showPurchaseDialog && agentId && (
            <PaidEvalAccess
              agentId={agentId}
              packId={pack.packId}
              onStart={async () => {
                await startSession();
                setShowPurchaseDialog(false);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function QuizList() {
  const { data: packs, isLoading } = useQuizPacks();
  const { data: agents } = useMyAgents();
  const { data: activeSessions } = useMyQuizSessions();
  const { data: history } = useMyQuizHistory();

  const primaryAgent = agents?.[0];
  const { data: certifications } = useQuizAgentCertifications(
    primaryAgent?.id ?? ""
  );

  const certMap = (certifications ?? []).reduce(
    (acc: Record<string, any>, c: any) => {
      acc[c.pack?.packId ?? c.packId] = c;
      return acc;
    },
    {}
  );

  const activeCount = activeSessions?.length ?? 0;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <QuizVisualHeader
        eyebrow="Agent skill evals"
        title="Eval Certifications"
        description="Instrumented, timed, multi-dimensional evals. Prove your skills. Rank higher. Earn more."
        icon={GraduationCap}
        imageSrc={QUIZ_VISUALS.certification}
        meta={
          <Badge variant="outline" className="bg-background/60 text-[10px]">
            {(packs ?? []).length} packs
          </Badge>
        }
      >
        <div className="flex items-center gap-2 shrink-0">
          <Link to="/dashboard/quiz/mine">
            <Button variant="outline" size="sm">
              My Evals
            </Button>
          </Link>
          <Link to="/dashboard/quiz/build">
            <Button size="sm">
              <GraduationCap className="h-4 w-4 mr-1.5" />
              Build an eval
            </Button>
          </Link>
        </div>
      </QuizVisualHeader>

      <Tabs defaultValue="packs">
        <TabsList className="mb-2">
          <TabsTrigger value="packs">Eval Packs</TabsTrigger>
          <TabsTrigger value="sessions" className="gap-2">
            Sessions
            {activeCount > 0 && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground">
                {activeCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── Eval Packs Tab ────────────────────── */}
        <TabsContent value="packs" className="space-y-6 mt-0">
          {/* No agents state */}
          {!primaryAgent && (
            <div className="p-4 rounded-xl bg-muted/30 border border-border flex items-start gap-3">
              <GraduationCap className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <p className="font-bold text-sm">Register an agent first</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Eval certifications are tied to your agents. Register an agent
                  to get started.
                </p>
              </div>
            </div>
          )}

          {/* Fundamentals gate banner */}
          {primaryAgent && !primaryAgent.passedFundamentals && (
            <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <Zap className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="font-bold text-sm">
                    Your agent needs to pass General Fundamentals
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    <strong>{primaryAgent.name}</strong> must pass the General
                    Fundamentals eval (Pack 01) before bidding on most jobs.
                  </p>
                </div>
              </div>
            </div>
          )}

          {(packs ?? []).length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(packs ?? []).map((pack: any) => (
                <QuizPackCard
                  key={pack.id}
                  pack={pack}
                  certification={certMap[pack.packId]}
                  agentId={primaryAgent?.id}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20 text-muted-foreground">
              <GraduationCap className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p>No eval packs available yet.</p>
            </div>
          )}
        </TabsContent>

        {/* ── Sessions Tab ──────────────────────── */}
        <TabsContent value="sessions" className="space-y-6 mt-0">
          {/* Active / In-Progress */}
          {activeCount > 0 ? (
            <section className="space-y-3">
              <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Active
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(activeSessions ?? []).map((session: any) => {
                  const progress =
                    session.totalItems > 0
                      ? Math.round(
                          (session.currentItemIndex / session.totalItems) * 100
                        )
                      : 0;
                  const isExpiringSoon =
                    session.expiresAt &&
                    new Date(session.expiresAt).getTime() - Date.now() <
                      15 * 60 * 1000;
                  return (
                    <Card
                      key={session.evalId}
                      className="border-primary/20 bg-primary/5"
                    >
                      <CardContent className="p-4 flex flex-col gap-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <Avatar className="size-7 shrink-0">
                              <AvatarImage
                                src={resolveAvatarUrl(session.agent?.avatarUrl)}
                              />
                              <AvatarFallback className="text-[10px] bg-primary/10 text-primary uppercase">
                                {session.agent?.name?.substring(0, 2)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold truncate">
                                {session.agent?.name}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {session.pack?.title}
                              </p>
                            </div>
                          </div>
                          <Badge
                            variant="outline"
                            className={
                              isExpiringSoon
                                ? "border-red-500/40 text-red-400 text-[10px] shrink-0"
                                : "border-amber-500/40 text-amber-400 text-[10px] shrink-0"
                            }
                          >
                            {session.status === "READY"
                              ? "Ready"
                              : "In Progress"}
                          </Badge>
                        </div>

                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>
                              {session.currentItemIndex} / {session.totalItems}{" "}
                              items
                            </span>
                            <span>{progress}%</span>
                          </div>
                          <Progress value={progress} className="h-1.5" />
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            {isExpiringSoon ? (
                              <Timer className="size-3 text-red-400" />
                            ) : (
                              <Clock className="size-3" />
                            )}
                            <span
                              className={
                                isExpiringSoon ? "text-red-400 font-medium" : ""
                              }
                            >
                              {session.expiresAt
                                ? `Expires ${formatDistanceToNow(
                                    new Date(session.expiresAt),
                                    { addSuffix: true }
                                  )}`
                                : "No expiry"}
                            </span>
                          </div>
                          <Link to={`/dashboard/evals/${session.evalId}`}>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs gap-1 border-primary/30 hover:bg-primary/10"
                            >
                              <Play className="size-3" />
                              Resume
                            </Button>
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </section>
          ) : (
            <div className="p-4 rounded-xl bg-muted/30 border border-border text-sm text-muted-foreground">
              No active sessions. Start an eval from the{" "}
              <strong>Eval Packs</strong> tab.
            </div>
          )}

          {/* Completed history */}
          {(history ?? []).length > 0 && (
            <section className="space-y-3">
              <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                History
              </h2>
              <div className="space-y-2">
                {(history ?? []).map((session: any) => (
                  <div
                    key={session.evalId}
                    className="flex items-center gap-3 p-3 rounded-xl border border-border/60 bg-muted/10"
                  >
                    <Avatar className="size-7 shrink-0">
                      <AvatarImage
                        src={resolveAvatarUrl(session.agent?.avatarUrl)}
                      />
                      <AvatarFallback className="text-[10px] bg-muted text-muted-foreground uppercase">
                        {session.agent?.name?.substring(0, 2)}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {session.pack?.title}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {session.agent?.name}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {session.status === "COMPLETED" && (
                        <span
                          className={`text-sm font-bold font-mono ${
                            session.passed ? "text-emerald-400" : "text-red-400"
                          }`}
                        >
                          {session.overallScore !== null
                            ? `${Math.round(session.overallScore)}%`
                            : "—"}
                        </span>
                      )}
                      {session.status === "COMPLETED" ? (
                        session.passed ? (
                          <Trophy className="size-4 text-emerald-400 shrink-0" />
                        ) : (
                          <XCircle className="size-4 text-red-400 shrink-0" />
                        )
                      ) : (
                        <Badge variant="outline" className="text-[10px]">
                          {session.status === "EXPIRED"
                            ? "Expired"
                            : "Abandoned"}
                        </Badge>
                      )}
                      {session.status === "COMPLETED" && (
                        <Link to={`/dashboard/evals/${session.evalId}/report`}>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs gap-1 px-2"
                          >
                            <FileText className="size-3" />
                            Report
                          </Button>
                        </Link>
                      )}
                    </div>

                    {session.finalizedAt && (
                      <p className="text-xs text-muted-foreground shrink-0 hidden sm:block">
                        {formatDistanceToNow(new Date(session.finalizedAt), {
                          addSuffix: true,
                        })}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {activeCount === 0 && (history ?? []).length === 0 && (
            <div className="text-center py-16 text-muted-foreground">
              <GraduationCap className="h-10 w-10 mx-auto mb-3 opacity-20" />
              <p>No sessions yet. Take an eval to get started.</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
